import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Design, DesignLogo } from "../../../../core/models/entities/Design";
import { CreateDesignDto } from "../../../../core/models/dtos/CreateDesignDto";
import { InitiateApproval } from "../../logo-list/hooks/useLogoActionsHandler";
import { useOrderActionHandler } from "../../order-create/hooks/useOrderActionsHandler";
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { withToasts } from "../../../components/Toast/Toast";
import { Role } from "../../../../core/models/entities/Role";
import { useApiClient } from "../../../../data/clients/ApiClient";
import { mapToDesign } from "../../../../data/models/mappers/mapDesign";
import { useCartHidden } from "../../../../hooks/useCartHidden";
import { toast } from "react-toastify";

interface DesignActionHandler {
  isLoading: boolean;
  handleCopyDesign: (design: Design) => Promise<Design>;
  handleCreateDesign: (design: CreateDesignDto) => Promise<Design>;
  handleGoToCreateDesign: (id?: number) => void;
  handleGoToDesignList: () => void;
  handleUpdateDesign: (design: Design) => Promise<Design>;
  handleDeleteDesign: (design: Design) => Promise<void>;
  handleGoToUpdateDesign: (design: Design, addLogoId?: number) => void;
  handleAddToCart: (design: Design, quantity: number) => Promise<void>;
  handleAddItemsToCart: (designs: Design[], quantity: number) => Promise<void>;
  handleInitiateApproval: (
    design: Design,
    approval: InitiateApproval
  ) => Promise<void>;
  handlePlaceVerdict: (design: Design, verdict: Verdict) => Promise<Design>;
  handleDeleteLogo: (design: Design) => Promise<void>;
  handleUpdateThenCommit: (design: Design) => Promise<Design>;
  handleCommit: (design: Design) => Promise<Design>;
  handleApprovalSkip: (design: Design) => Promise<Design>;
  handleSendApproval: (design: Design, email: string) => Promise<void>;
  handleSendApprovalWithEndCustomerPending: (
    design: Design,
    email: string
  ) => Promise<void>;
  handleChangeStyle(
    orgId: number,
    designId: number,
    styleId: number
  ): Promise<Design>;
  handleChangeTitle(
    orgId: number,
    designId: number,
    title: string
  ): Promise<Design>;
  handleSkipAddToCart(design: Design, quantity: number): Promise<Design>;
  handleRejectAndDelete: (design: Design, note?: string) => Promise<void>;
}

export function useDesignActionHandler(): DesignActionHandler {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { profile } = useProfileContext();
  const role = profile?.userOrganizationInformations.role;

  const { designRepository } = useRepositoriesContext();
  const { handleDesignAddOrderLine, handleDesignAddOrderLines } =
    useOrderActionHandler();
  const apiClient = useApiClient();
  const cartHidden = useCartHidden();

  function handleCopyDesign(design: Design) {
    async function transaction() {
      return await designRepository.copyDesign(design.customerId, design.id);
    }

    return withToasts(
      transaction,
      "Copying design",
      "Design copied",
      "Failed to copy design",
      setIsLoading
    );
  }

  function handleCreateDesign(design: CreateDesignDto): Promise<Design> {
    async function transaction() {
      return await designRepository.createDesign({ ...design });
    }

    return withToasts(
      transaction,
      "Creating design",
      "Design created",
      "Failed to create design",
      setIsLoading
    );
  }

  function handleChangeStyle(
    orgId: number,
    designId: number,
    styleId: number
  ): Promise<Design> {
    async function transaction() {
      return await designRepository.changeStyle(orgId, designId, styleId);
    }

    return withToasts(
      transaction,
      "Updating design style",
      "Design style updated",
      "Failed to update design style",
      setIsLoading
    );
  }

  function handleChangeTitle(orgId: number, designId: number, title: string) {
    async function transaction() {
      return await designRepository.changeTitle(orgId, designId, title);
    }

    return withToasts(
      transaction,
      "Updating design title",
      "Design title updated",
      "Failed to update design title",
      setIsLoading
    );
  }

  function handleAddToCart(design: Design, quantity: number): Promise<void> {
    if (cartHidden) {
      toast.error("Cart is not available for this account.");
      return Promise.resolve();
    }
    async function transaction() {
      let orgId: number;

      if (profile?.userOrganizationInformations.organizationId !== undefined) {
        orgId = profile.userOrganizationInformations.organizationId;
      } else if (role === 1) {
        orgId = design.vendor?.id ?? 0;
      } else if (role === 0) {
        orgId = design.customer?.id ?? 0;
      } else if (role === 2) {
        orgId = 0;
      } else {
        orgId = 0;
      }

      const designDto = await apiClient.getDesign(orgId, design.id);
      const fullDesign = mapToDesign(designDto);
      const deletedDesignLogos = fullDesign.designLogos.filter(
        (dl) => dl.logo.isDeleted,
      );
      const filteredDesign: Design = {
        ...fullDesign,
        designLogos: fullDesign.designLogos.filter((dl) => !dl.logo.isDeleted),
      };
      handleDesignAddOrderLine(
        filteredDesign,
        quantity,
        undefined,
        deletedDesignLogos.length > 0 ? deletedDesignLogos : undefined,
      );
    }

    return withToasts(
      transaction,
      "Adding to cart",
      "Added to cart",
      "Failed to add to cart",
      setIsLoading
    );
  }

  function handleAddItemsToCart(
    designs: Design[],
    quantity: number
  ): Promise<void> {
    if (cartHidden) {
      toast.error("Cart is not available for this account.");
      return Promise.resolve();
    }
    const excludedDeletedDesignLogos = new Map<number, DesignLogo[]>();
    const filteredDesigns = designs.map((design) => {
      const deletedLogos = design.designLogos.filter((dl) => dl.logo.isDeleted);
      if (deletedLogos.length > 0) {
        excludedDeletedDesignLogos.set(design.id, deletedLogos);
      }
      return {
        ...design,
        designLogos: design.designLogos.filter((dl) => !dl.logo.isDeleted),
      };
    });
    handleDesignAddOrderLines(
      filteredDesigns,
      quantity,
      undefined,
      excludedDeletedDesignLogos.size > 0
        ? excludedDeletedDesignLogos
        : undefined,
    );
    return Promise.resolve();
  }

  function handleUpdateDesign(design: Design) {
    async function transaction() {
      return await designRepository.updateDesign(design);
    }

    return withToasts(
      transaction,
      "Updating design",
      "Design updated",
      "Failed to update design",
      setIsLoading
    );
  }

  function handleDeleteDesign(design: Design) {
    async function transaction() {
      return await designRepository.deleteDesign(design.customerId, design.id);
    }

    return withToasts(
      transaction,
      "Deleting design",
      "Design deleted",
      "Failed to delete design",
      setIsLoading
    );
  }

  function handleGoToCreateDesign(id?: number) {
    navigate("/design/create" + (id ? `/${id}` : ""));
  }

  function handleGoToDesignList() {
    navigate("/design");
  }

  function handleGoToUpdateDesign(design: Design, addLogoId?: number) {
    const query = addLogoId ? `&addLogoId=${addLogoId}` : "";
    navigate(`/design/${design.id}/editor?orgId=${design.customer.id}${query}`);
  }

  function handleUpdateThenCommit(design: Design) {
    async function transaction() {
      await designRepository.updateDesign(design);
      return await designRepository.commit(design);
    }

    return withToasts(
      transaction,
      "Updating and committing design",
      "Design updated and committed",
      "Failed to update and commit design",
      setIsLoading
    );
  }

  function handleCommit(design: Design) {
    async function transaction() {
      return await designRepository.commit(design);
    }

    return withToasts(
      transaction,
      "Committing design",
      "Design committed",
      "Failed to commit design",
      setIsLoading
    );
  }

  function handleDeleteLogo(design: Design) {
    async function transaction() {
      return await designRepository.deleteDesign(design.customer.id, design.id);
    }

    return withToasts(
      transaction,
      "Deleting design",
      "Design deleted",
      "Failed to design logo",
      setIsLoading
    );
  }

  function handlePlaceVerdict(design: Design, verdict: Verdict) {
    async function transaction() {
      return await designRepository.placeVerdict(design, verdict);
    }

    return withToasts(
      transaction,
      "Updating approval",
      "Verdict successful",
      "Approval failed",
      setIsLoading
    );
  }

  function handleInitiateApproval(design: Design, approval: InitiateApproval) {
    async function transaction() {
      const approvedDesign = await designRepository.initiateApproval(
        design,
        approval
      );
      // return await designRepository.placeVerdict(approvedDesign, { approve: true })
    }

    return withToasts(
      transaction,
      "Updating approval",
      "Approval successful",
      "Approval failed",
      setIsLoading
    );
  }

  function handleApprovalSkip(design: Design): Promise<Design> {
    async function transaction() {
      // Vendors and Admins can truly skip approval, Customers send for approval
      const shouldSkip = role === Role.Admin || role === Role.Vendor;

      return await designRepository.initiateApproval(design, {
        skip: shouldSkip,
        email: undefined,
      });
    }

    const loadingMessage =
      role === Role.Admin || role === Role.Vendor
        ? "Skipping approval"
        : "Creating design";
    const successMessage =
      role === Role.Admin || role === Role.Vendor
        ? "Approval skipped successfully"
        : "Design created successfully";
    const errorMessage =
      role === Role.Admin || role === Role.Vendor
        ? "Approval skip failed"
        : "Sending for approval failed";

    return withToasts(
      transaction,
      loadingMessage,
      successMessage,
      errorMessage,
      setIsLoading
    );
  }

  function handleSendApproval(design: Design, email: string) {
    async function transaction() {
      return await designRepository.sendApproval(design, email);
    }

    return withToasts(
      transaction,
      "Sending email",
      "Email send successful",
      "Email send failed",
      setIsLoading
    );
  }

  function handleSendApprovalWithEndCustomerPending(
    design: Design,
    email: string
  ) {
    async function transaction() {
      // Use conditional approval that preserves existing approval statuses
      return await designRepository.sendApprovalConditional(design, email);
    }

    return withToasts(
      transaction,
      "Sending approval",
      "Approval send successful",
      "Approval send failed",
      setIsLoading
    );
  }

  function handleRejectAndDelete(design: Design, note?: string) {
    async function transaction() {
      const placedVerdict = await designRepository.placeVerdict(design, {
        approve: false,
        rejectionNote: note ?? "",
      });
      return await designRepository.deleteDesign(
        placedVerdict.customerId,
        design.id
      );
    }

    return withToasts(
      transaction,
      "Deleting design",
      "Deletion successful",
      "Deletion failed",
      setIsLoading
    );
  }

  function handleSkipAddToCart(design: Design, quantity: number) {
    if (cartHidden) {
      toast.error("Cart is not available for this account.");
      return Promise.reject(new Error("Cart hidden"));
    }
    async function transaction() {
      // For Customers, we need to send for approval to Vendor first, then add to cart
      // For Admins/Vendors, we can truly skip approval and add to cart directly
      const shouldSkip = role === Role.Admin || role === Role.Vendor;

      return await designRepository
        .initiateApproval(design, {
          skip: shouldSkip,
          email: undefined,
        })
        .then((updatedDesign) => {
          handleAddToCart(updatedDesign, quantity);
          return updatedDesign;
        });
    }

    const loadingMessage =
      role === Role.Admin || role === Role.Vendor
        ? "Finishing design"
        : "Sending approval";
    const successMessage =
      role === Role.Admin || role === Role.Vendor
        ? "Design finished successfully"
        : "Design sent for approval";
    const errorMessage =
      role === Role.Admin || role === Role.Vendor
        ? "Approval skip failed"
        : "Sending for approval failed";

    return withToasts(
      transaction,
      loadingMessage,
      successMessage,
      errorMessage,
      setIsLoading
    );
  }

  return {
    isLoading,
    handleCopyDesign,
    handleCreateDesign,
    handleGoToCreateDesign,
    handleChangeTitle,
    handleUpdateDesign,
    handleDeleteDesign,
    handleGoToUpdateDesign,
    handleAddToCart,
    handleAddItemsToCart,
    handleGoToDesignList,
    handleInitiateApproval,
    handlePlaceVerdict,
    handleDeleteLogo,
    handleUpdateThenCommit,
    handleCommit,
    handleApprovalSkip,
    handleSendApproval,
    handleSendApprovalWithEndCustomerPending,
    handleChangeStyle,
    handleSkipAddToCart,
    handleRejectAndDelete,
  };
}
